import { AfterViewInit, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as mapboxgl from 'mapbox-gl';
import { LohiDashboardService, MappableDriver } from '../lohi-dashboard.service';

@Component({
  selector: 'lohi-app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements AfterViewInit, OnChanges {
  @Input() public drivers: MappableDriver[] = [];
  @Input() public selectedDriver: MappableDriver | null = null;
  private driverMarkers: Map<string, mapboxgl.Marker> = new Map();

  public map!: mapboxgl.Map;
  private accessToken = 'pk.eyJ1Ijoic2F0ZXN0ZGV2IiwiYSI6ImNrYmVraDllZDBtcXQycHFlbzVqYXk0cnMifQ.HzVySDvy0mLDW03ykD2HNw';
  private markers: mapboxgl.Marker[] = [];

  private currentPopup: mapboxgl.Popup | null = null;

  constructor(private dashboardService: LohiDashboardService) {}

  public ngAfterViewInit(): void {
    (mapboxgl as any).accessToken = this.accessToken;

    const inProgressDriver = this.drivers.find((driver) => driver.status === 'in-progress' && driver.lastWaypoint);
    const initialCenter = inProgressDriver
      ? [inProgressDriver.lastWaypoint.longitude, inProgressDriver.lastWaypoint.latitude]
      : [-104.9903, 39.7392]; // Default to Denver, USA

    this.map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox://styles/mapbox/streets-v11',
      center: initialCenter as [number, number],
      zoom: 12,
    });

    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.on('load', () => {
      this.updateDriverMarkers();
      this.loadHeatmap();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.drivers && this.map) {
      this.updateDriverMarkers();
    }

    if (changes?.selectedDriver && this.selectedDriver && this.map) {
      this.centerOnDriver(this.selectedDriver);
    }
  }

  private updateDriverMarkers(): void {
    if (!this.map) {
      return;
    }

    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
    this.driverMarkers.clear();

    const processedDriverIds = new Set();

    this.drivers.forEach((driver) => {
      if (processedDriverIds.has(driver.driverId)) {
        return;
      }
      processedDriverIds.add(driver.driverId);

      if (driver.lastWaypoint) {
        const { latitude, longitude } = driver.lastWaypoint;

        const markerElement = document.createElement('div');
        markerElement.className = `custom-marker ${driver.status}`;
        markerElement.style.width = '30px';
        markerElement.style.height = '30px';

        const statusIcon = {
          'in-progress': './assets/icons/new-dashboard/driver-marker.svg',
          'on-shift': './assets/icons/new-dashboard/driver-marker.svg',
          'on-hitch': './assets/icons/new-dashboard/driver-marker.svg',
        }[driver.status];

        markerElement.style.backgroundImage = `url(${statusIcon})`;
        markerElement.style.backgroundSize = 'contain';

        const marker = new mapboxgl.Marker(markerElement).setLngLat([longitude, latitude]).addTo(this.map);
        this.markers.push(marker);
        this.driverMarkers.set(driver.driverId, marker);
      }
    });
  }

  private centerOnDriver(driver: MappableDriver): void {
    if (!this.map || !driver?.lastWaypoint) {
      return;
    }

    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }

    this.driverMarkers.forEach((m) => {
      m.getElement().classList.remove('selected-marker');
    });

    const { latitude, longitude } = driver.lastWaypoint;

    this.map.flyTo({
      center: [longitude, latitude],
      zoom: 15,
      essential: true,
    });

    const marker = this.driverMarkers.get(driver.driverId);
    if (marker) {
      marker.getElement().classList.add('selected-marker');

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: false,
      }).setHTML(`<div class="font-bold text-white text-base text-center">${driver.driverName}</div>`);

      marker.setPopup(popup);
      marker.togglePopup();

      this.currentPopup = popup;
    }
  }

  private loadHeatmap(): void {
    this.dashboardService.getCarrierHeatmap$().subscribe((geoJsonData) => {
      if (
        !geoJsonData ||
        !geoJsonData.features ||
        !Array.isArray(geoJsonData.features) ||
        geoJsonData.features.length === 0
      ) {
        return;
      }

      if (!this.map.getSource('heatmap')) {
        this.map.addSource('heatmap', {
          type: 'geojson',
          data: geoJsonData,
        });

        this.map.addLayer({
          id: `heatmaps`,
          type: 'circle',
          source: `heatmap`,
          paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 10, 10, 50, 15, 100],
            'circle-color': 'rgba(255, 0, 0, 0.5)',
          },
        });
      } else {
        const source = this.map.getSource('heatmap') as mapboxgl.GeoJSONSource;
        source.setData(geoJsonData);
      }
    });
  }
}
